import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { blogAPI } from '../../services/api';
import { ImageUpload } from '../ui/image-upload';
import { RichTextEditor } from '../ui/rich-text-editor';

const BlogTab = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [postForm, setPostForm] = useState({ title: '', excerpt: '', category: '', content: '', image: '', readTime: '' });

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      const response = await blogAPI.getAll();
      setPosts(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle news');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!postForm.image) {
      toast.error('Carica un\'immagine per la news');
      return;
    }
    try {
      if (editingPost) {
        await blogAPI.update(editingPost.id, postForm);
        toast.success('News aggiornata!');
      } else {
        await blogAPI.create(postForm);
        toast.success('News creata!');
      }
      loadPosts();
      setIsDialogOpen(false);
      setEditingPost(null);
      setPostForm({ title: '', excerpt: '', category: '', content: '', image: '', readTime: '' });
    } catch (error) {
      toast.error('Errore nel salvare la news');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa news?')) return;
    try {
      await blogAPI.delete(id);
      toast.success('News eliminata!');
      loadPosts();
    } catch (error) {
      toast.error('Errore nell\'eliminare la news');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setPostForm({
      title: post.title,
      excerpt: post.excerpt,
      category: post.category,
      content: post.content,
      image: post.image,
      readTime: post.readTime
    });
    setIsDialogOpen(true);
  };

  return (
    <Card className="glass-card">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
          <div>
            <CardTitle className="text-2xl">Gestione News</CardTitle>
            <CardDescription>Aggiungi, modifica o elimina articoli</CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="add-blog-btn"
                onClick={() => { 
                  setEditingPost(null); 
                  setPostForm({ title: '', excerpt: '', category: '', content: '', image: '', readTime: '' }); 
                }}
              >
                <Plus className="w-4 h-4 mr-2" />Aggiungi News
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingPost ? 'Modifica News' : 'Nuova News'}</DialogTitle>
                <DialogDescription>{editingPost ? 'Aggiorna le informazioni' : 'Compila i dettagli'}</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="title">Titolo</Label>
                  <Input 
                    id="title" 
                    data-testid="blog-title-input"
                    value={postForm.title} 
                    onChange={(e) => setPostForm({ ...postForm, title: e.target.value })} 
                    required 
                    className="mt-2" 
                  />
                </div>
                <div>
                  <Label htmlFor="category">Categoria</Label>
                  <Input 
                    id="category" 
                    data-testid="blog-category-input"
                    value={postForm.category} 
                    onChange={(e) => setPostForm({ ...postForm, category: e.target.value })} 
                    required 
                    className="mt-2" 
                  />
                </div>
                <div>
                  <Label htmlFor="excerpt">Estratto</Label>
                  <Textarea 
                    id="excerpt" 
                    data-testid="blog-excerpt-input"
                    value={postForm.excerpt} 
                    onChange={(e) => setPostForm({ ...postForm, excerpt: e.target.value })} 
                    required 
                    rows={2} 
                    className="mt-2 resize-none" 
                  />
                </div>
                <div>
                  <Label htmlFor="content">Contenuto Completo</Label>
                  <div className="mt-2">
                    <RichTextEditor
                      value={postForm.content}
                      onChange={(value) => setPostForm({ ...postForm, content: value })}
                      placeholder="Scrivi il contenuto dell'articolo..."
                      minHeight="250px"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="readTime">Tempo di Lettura</Label>
                  <Input 
                    id="readTime" 
                    data-testid="blog-readtime-input"
                    value={postForm.readTime} 
                    onChange={(e) => setPostForm({ ...postForm, readTime: e.target.value })} 
                    required 
                    className="mt-2" 
                    placeholder="5 min" 
                  />
                </div>
                <div>
                  <Label>Immagine</Label>
                  <div className="mt-2">
                    <ImageUpload
                      value={postForm.image}
                      onChange={(url) => setPostForm({ ...postForm, image: url })}
                      uploadType="image"
                      label="Carica immagine news"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Annulla</Button>
                  <Button type="submit" data-testid="submit-blog-btn">{editingPost ? 'Aggiorna' : 'Crea'} News</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Immagine</TableHead>
                <TableHead>Titolo</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead>Data</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8">Caricamento...</TableCell></TableRow>
              ) : posts.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Nessuna news</TableCell></TableRow>
              ) : (
                posts.map((post) => (
                  <TableRow key={post.id} data-testid={`blog-row-${post.id}`}>
                    <TableCell><img src={post.image} alt={post.title} className="w-12 h-12 rounded-md object-cover" /></TableCell>
                    <TableCell className="font-medium">{post.title}</TableCell>
                    <TableCell><Badge variant="secondary">{post.category}</Badge></TableCell>
                    <TableCell>{new Date(post.created_at).toLocaleDateString('it-IT')}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(post)} data-testid={`edit-blog-${post.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(post.id)} className="text-destructive hover:text-destructive" data-testid={`delete-blog-${post.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default BlogTab;
